import React, { useEffect, useState } from "react";
import Layout from "../layout/Layout";
import {
	Form,
	Input,
	Button,
	Flex,
	Typography,
	notification,
	Result,
	Spin,
} from "antd";
import { LockOutlined, CheckCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { api } from "../utils/Api";

const { Title, Text } = Typography;

const ResetPassword = ({ history, location }) => {
	const [loading, setLoading] = useState(false);
	const [checking, setChecking] = useState(true);
	const [verified, setVerified] = useState(false);
	const [success, setSuccess] = useState(false);
	const [otpValue, setOtpValue] = useState("");

	const [apiNotification, contextHolder] = notification.useNotification();

	const [codeForm] = Form.useForm();
	const [passwordForm] = Form.useForm();

	const type = location?.state?.type;
	const credential = location?.state?.credential;

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message,
			description,
		});
	};

	useEffect(() => {
		validateResetSession();
	}, []);

	const validateResetSession = async () => {
		try {
			if (!type || !credential) {
				setChecking(false);
				return;
			}
		} catch (err) {
			openNotification(
				"error",
				"Error",
				err?.response?.data?.message || "Invalid session.",
			);
		} finally {
			setChecking(false);
		}
	};

	const verifyCode = async (values) => {
		try {
			if (!otpValue || otpValue.length !== 6) {
				return openNotification(
					"error",
					"Error",
					"Verification code must be 6 digits.",
				);
			}

			setLoading(true);

			const body = {
				type,
				credential,
				otpCode: otpValue,
			};

			const { data } = await axios.post(`${api}/auth/verify-reset-code`, body);

			if (data.success) {
				localStorage.setItem("resetPasswordToken", data.payload);
				openNotification("success", "Success", "Code verified successfully.");

				setVerified(true);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification(
				"error",
				"Error",
				err?.response?.data?.message || "Invalid verification code.",
			);
		} finally {
			setLoading(false);
		}
	};

	const updatePassword = async (values) => {
		try {
			setLoading(true);

			const body = {
				type,
				credential,
				password: values.password,
			};

			const token = localStorage.getItem("resetPasswordToken");

			const { data } = await axios.post(`${api}/auth/reset-password`, body, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (data.success) {
				setSuccess(true);

				localStorage.removeItem("resetPasswordToken");

				openNotification(
					"success",
					"Success",
					"Password updated successfully.",
				);

				setTimeout(() => {
					history.push("/signin");
				}, 2000);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			openNotification(
				"error",
				"Error",
				err?.response?.data?.message || "Could not update password.",
			);
		} finally {
			setLoading(false);
		}
	};

	// VALIDANDO SESSION
	if (checking) {
		return (
			<Layout title="" description="" type="small">
				<Flex
					vertical
					align="center"
					justify="center"
					style={{ minHeight: 300 }}
					gap={20}
				>
					<Spin size="large" />
					<Text>Validating session...</Text>
				</Flex>
			</Layout>
		);
	}

	// FALTAN DATOS
	if (!type || !credential) {
		return (
			<Layout title="" description="" type="small">
				<Result
					status="error"
					title="Invalid Reset Session"
					subTitle="Missing credential or verification type."
					extra={[
						<Button
							type="primary"
							key="signin"
							onClick={() => history.push("/forgot-password")}
						>
							Back to Forgot Password
						</Button>,
					]}
				/>
			</Layout>
		);
	}

	// SUCCESS
	if (success) {
		return (
			<Layout title="" description="" type="small">
				<Result
					status="success"
					icon={<CheckCircleOutlined />}
					title="Password Updated Successfully"
					subTitle="Your password has been updated successfully."
					extra={[
						<Button
							type="primary"
							key="signin"
							size="large"
							onClick={() => history.push("/signin")}
						>
							Go to Sign In
						</Button>,
					]}
				/>
			</Layout>
		);
	}

	return (
		<Layout title="" description="" type="small">
			<Flex vertical gap={10}>
				<Title level={3} style={{ textAlign: "center", marginBottom: 0 }}>
					Reset Password
				</Title>

				<Text
					type="secondary"
					style={{ textAlign: "center", marginBottom: 20 }}
				>
					{!verified
						? "Enter the verification code sent to your account."
						: "Create your new password."}
				</Text>

				{!verified && (
					<Form form={codeForm} layout="vertical">
						<Form.Item
							label="Verification Code"
							name="code"
							validateStatus={otpValue && otpValue.length !== 6 ? "error" : ""}
							help={
								otpValue && otpValue.length !== 6
									? "Verification code must be 6 digits."
									: ""
							}
							style={{ display: "flex", justifyContent: "center" }}
						>
							<Input.OTP
								length={6}
								size="large"
								inputMode="numeric"
								mask={false}
								value={otpValue}
								onInput={(value) => {
									const otpString = Array.isArray(value)
										? value.join("")
										: value;
									setOtpValue(otpString);
								}}
								style={{
									width: "100%",
									justifyContent: "center",
								}}
							/>
						</Form.Item>

						<Form.Item>
							<Button
								type="primary"
								htmlType="button"
								size="large"
								block
								loading={loading}
								onClick={verifyCode}
							>
								Verify Code
							</Button>
						</Form.Item>
					</Form>
				)}

				{verified && (
					<Form form={passwordForm} layout="vertical" onFinish={updatePassword}>
						<Form.Item
							label="New Password"
							name="password"
							rules={[
								{
									required: true,
									message: "Please input your new password.",
								},
								{
									min: 6,
									message: "Password must be at least 6 characters.",
								},
							]}
							hasFeedback
						>
							<Input.Password
								size="large"
								prefix={<LockOutlined />}
								placeholder="Enter new password"
							/>
						</Form.Item>

						<Form.Item
							label="Confirm Password"
							name="confirmPassword"
							dependencies={["password"]}
							hasFeedback
							rules={[
								{
									required: true,
									message: "Please confirm your password.",
								},
								({ getFieldValue }) => ({
									validator(_, value) {
										if (!value || getFieldValue("password") === value) {
											return Promise.resolve();
										}

										return Promise.reject(new Error("Passwords do not match."));
									},
								}),
							]}
						>
							<Input.Password
								size="large"
								prefix={<LockOutlined />}
								placeholder="Confirm new password"
							/>
						</Form.Item>

						<Form.Item>
							<Button
								type="primary"
								htmlType="submit"
								size="large"
								block
								loading={loading}
							>
								Update Password
							</Button>
						</Form.Item>
					</Form>
				)}
			</Flex>

			{contextHolder}
		</Layout>
	);
};

export default ResetPassword;
